/**
 * PushToRepoModal
 * Modal to push generated agents, skills, and Copilot instructions to GitHub as a PR.
 * Shows file previews and push progress.
 */

import React, { useState, useEffect } from 'react';
import {
  GitPullRequest,
  FileCode,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Loader,
} from 'lucide-react';
import { Modal, Button, Input, Textarea, Checkbox } from '../../ui';
import { developerAgentApi } from '../../../services/api';
import type { PushOptions, PushFilePreview, PushStatus } from '../../../types';
import styles from './PushToRepoModal.module.css';

export interface PushToRepoModalProps {
  analysisId: string;
  repoName: string;
  onClose: () => void;
  onComplete: (prUrl?: string) => void;
}

export const PushToRepoModal: React.FC<PushToRepoModalProps> = ({
  analysisId,
  repoName,
  onClose,
  onComplete,
}) => {
  // Form state
  const [options, setOptions] = useState<PushOptions>({
    branchName: `bringiq/agents-${Date.now()}`,
    prTitle: `[BringIQ] Add generated agents and skills`,
    prDescription: `This PR adds AI-generated developer agents and skills based on codebase analysis.\n\nGenerated by BringIQ Developer Agents.`,
    includeCopilotInstructions: true,
    includeAgents: true,
    includeSkills: true,
    includePrompts: true,
  });

  // Preview & push state
  const [filePreview, setFilePreview] = useState<PushFilePreview[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [isPushing, setIsPushing] = useState(false);
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'configure' | 'preview' | 'pushing' | 'done'>('configure');

  /** Load file preview */
  useEffect(() => {
    const loadPreview = async () => {
      setIsLoadingPreview(true);
      try {
        const response = await developerAgentApi.getPushFilePreview(analysisId);
        if (response.success && response.data) {
          setFilePreview(response.data);
        }
      } catch (err) {
        console.error('Failed to load file preview:', err);
      } finally {
        setIsLoadingPreview(false);
      }
    };
    loadPreview();
  }, [analysisId]);

  /** Handle option changes */
  const handleOptionChange = (field: keyof PushOptions, value: string | boolean) => {
    setOptions((prev) => ({ ...prev, [field]: value }));
  };

  /** Start push */
  const handlePush = async () => {
    setIsPushing(true);
    setError(null);
    setStep('pushing');

    try {
      const response = await developerAgentApi.pushToRepo(analysisId, options);
      if (response.success && response.data) {
        if (response.data.success) {
          setPushStatus({
            id: analysisId,
            status: 'complete',
            progress: 100,
            currentStep: 'Done',
            prUrl: response.data.prUrl,
          });
          setStep('done');
        } else {
          setError(response.data.error || 'Push failed');
          setStep('configure');
        }
      }
    } catch (err: any) {
      console.error('Failed to push to repo:', err);
      setError(err.message || 'Failed to push to repository');
      setStep('configure');
    } finally {
      setIsPushing(false);
    }
  };

  /** Render configure step */
  const renderConfigure = () => (
    <div className={styles.form}>
      <div className={styles.field}>
        <label className={styles.label}>Branch Name</label>
        <Input
          value={options.branchName}
          onChange={(e) => handleOptionChange('branchName', e.target.value)}
          placeholder="bringiq/agents"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>PR Title</label>
        <Input
          value={options.prTitle}
          onChange={(e) => handleOptionChange('prTitle', e.target.value)}
          placeholder="PR title"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>PR Description</label>
        <Textarea
          value={options.prDescription}
          onChange={(e) => handleOptionChange('prDescription', e.target.value)}
          placeholder="Describe the changes..."
          rows={3}
        />
      </div>

      <div className={styles.checkboxGroup}>
        <label className={styles.checkboxLabel}>
          <Checkbox
            checked={options.includeCopilotInstructions}
            onChange={(checked) => handleOptionChange('includeCopilotInstructions', checked)}
          />
          Include Copilot Instructions
        </label>
        <label className={styles.checkboxLabel}>
          <Checkbox
            checked={options.includeAgents}
            onChange={(checked) => handleOptionChange('includeAgents', checked)}
          />
          Include Agents
        </label>
        <label className={styles.checkboxLabel}>
          <Checkbox
            checked={options.includeSkills}
            onChange={(checked) => handleOptionChange('includeSkills', checked)}
          />
          Include Skills
        </label>
        <label className={styles.checkboxLabel}>
          <Checkbox
            checked={options.includePrompts}
            onChange={(checked) => handleOptionChange('includePrompts', checked)}
          />
          Include Prompts
        </label>
      </div>

      {/* File Preview */}
      {filePreview.length > 0 && (
        <div className={styles.previewSection}>
          <h4 className={styles.previewTitle}>
            <FileCode size={16} />
            Files to push ({filePreview.length})
          </h4>
          <div className={styles.fileList}>
            {filePreview.map((file, idx) => (
              <div key={idx} className={styles.fileItem}>
                <span className={`${styles.fileAction} ${styles[file.type]}`}>
                  {file.type === 'create' ? '+' : '~'}
                </span>
                <span className={styles.filePath}>{file.path}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoadingPreview && (
        <div className={styles.previewLoading}>
          <Loader size={16} className={styles.spinning} />
          Loading file preview...
        </div>
      )}

      {error && (
        <div className={styles.error}>
          <AlertCircle size={14} />
          {error}
        </div>
      )}
    </div>
  );

  /** Render pushing step */
  const renderPushing = () => (
    <div className={styles.pushingState}>
      <Loader size={32} className={styles.spinning} />
      <h3>Pushing to {repoName}...</h3>
      <p>Creating branch, committing files, and opening pull request</p>
    </div>
  );

  /** Render done step */
  const renderDone = () => (
    <div className={styles.doneState}>
      <CheckCircle size={40} className={styles.successIcon} />
      <h3>Pull Request Created!</h3>
      <p>Your agents and skills have been pushed to <strong>{repoName}</strong></p>
      {pushStatus?.prUrl && (
        <a
          href={pushStatus.prUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.prLink}
        >
          <GitPullRequest size={16} />
          View Pull Request
          <ExternalLink size={14} />
        </a>
      )}
    </div>
  );

  /** Get modal footer based on step */
  const getFooter = () => {
    if (step === 'done') {
      return (
        <div className={styles.footer}>
          <Button variant="primary" size="sm" onClick={() => onComplete(pushStatus?.prUrl)}>
            Done
          </Button>
        </div>
      );
    }

    if (step === 'pushing') {
      return null;
    }

    return (
      <div className={styles.footer}>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<GitPullRequest size={14} />}
          onClick={handlePush}
          isLoading={isPushing}
          disabled={!options.branchName.trim() || !options.prTitle.trim()}
        >
          Create Pull Request
        </Button>
      </div>
    );
  };

  return (
    <Modal
      isOpen={true}
      onClose={step === 'pushing' ? () => {} : onClose}
      title="Push to Repository"
      size="lg"
      footer={getFooter()}
    >
      {step === 'configure' && renderConfigure()}
      {step === 'preview' && renderConfigure()}
      {step === 'pushing' && renderPushing()}
      {step === 'done' && renderDone()}
    </Modal>
  );
};

export default PushToRepoModal;
